import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { IconSave } from "@/assets/icons";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Form } from "@/modules/irohana-components/elements/Form";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { background, divider, text } from "@/theme/colors";
import { AppRoutes } from "@/routes/config";
import { FooterActionBar } from "@/modules/irohana-components/elements/FooterActionBar";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import { ProductInformationSection } from "@/features/organization/product/components/product-form/ProductInformationSection";
import { ProductTypePriceSection } from "@/features/organization/product/components/product-form/ProductTypePriceSection";
import { ProductCombinationSection } from "@/features/organization/product/components/product-form/product-combination/ProductCombinationSection";
import { ProductRefundSection } from "@/features/organization/product/components/product-form/ProductRefundSection";
import { productSchema } from "@/features/organization/product/schema/productSchema";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { createProductApi, updateProductApi } from "@/api/services/main/organization/product";
import { convertFormToCreatePayload, convertFormToUpdatePayload } from "@/features/organization/product/utils/productForm";
import { hasDuplicateCombinations } from "@/features/organization/product/utils/productUtils";
import { COMBINATION_DUPLICATED_MESSAGE } from "@/features/organization/product/constants/productEnums";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { useRefundConditionsTriggerValidate } from "@/features/organization/product/hooks/useRefundConditionsTriggerValidate";
import { OrganizationContainer } from "@/features/organization/components/OrganizationContainer";

type TProductEditFormProps = {
  mode: "create" | "edit";
  defaultValues: TProductForm;
  /** Edit mode — controls where cancel returns and 固定商品 locking. */
  productId?: number;
  isDefault?: boolean;
  /** ティア対象 (cloned from master); drives the ティア segment / third combination column. */
  isTierDiscountTarget?: boolean;
  /** Page heading — the product name (edit) or 商品登録 (create). */
  title: string;
};

export const ProductEditForm = ({
  mode,
  defaultValues,
  productId,
  isDefault,
  isTierDiscountTarget = false,
  title,
}: TProductEditFormProps) => {
  const navigate = useNavigate();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);

  const backUrl = mode === "edit" && productId != null ? `${AppRoutes.organizationProduct}/${productId}` : AppRoutes.organizationProduct;

  const methods = useForm<TProductForm>({
    defaultValues,
    resolver: yupResolver(productSchema) as never,
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  useTriggerValidate({ methods, name: "unitPrice", triggerFields: ["discountTokutei2", "firstDiscountPrice"] });
  useRefundConditionsTriggerValidate({ methods });

  const onSubmit = async (data: TProductForm) => {
    // Block duplicate 組み合わせ conditions before hitting the API (mirrors the backend constraint).
    if (
      hasDuplicateCombinations(data.combinations, {
        nationality: data.nationalityEnabled,
        field: data.fieldEnabled,
        tier: data.variantEnabled,
      })
    ) {
      toast.error(COMBINATION_DUPLICATED_MESSAGE);
      return;
    }

    try {
      const opts = { isTierDiscountTarget };
      if (mode === "edit" && productId != null) {
        await updateProductApi(productId, convertFormToUpdatePayload(data, opts));
      } else {
        await createProductApi(convertFormToCreatePayload(data, opts));
      }
      toast.success(API_SUCCESS_MESSAGE);
      methods.reset(data);
      setBlockerEnabled(false);
      setRedirect(backUrl);
    } catch {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={productSchema} enableBlocker={blockerEnabled}>
      <OrganizationContainer gap={3} pb={4}>
        <Typography variant="head20Bold" color={text.primary}>
          {title}
        </Typography>

        <MenuNavigate
          contents={[
            { title: "商品基本情報", content: <ProductInformationSection isDefault={isDefault} /> },
            {
              title: "種別単価設定",
              content: (
                <Stack bgcolor={background.white} borderRadius={2} border={`1px solid ${divider.middle}`} overflow="hidden">
                  <ProductTypePriceSection isTierDiscountTarget={isTierDiscountTarget} />
                  <ProductCombinationSection isTierDiscountTarget={isTierDiscountTarget} />
                </Stack>
              ),
            },
            { title: "返金条件設定", content: <ProductRefundSection /> },
          ]}
          contentAffix={
            <FooterActionBar>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button variant="ghost" onClick={() => navigate(backUrl)}>
                  戻る
                </Button>
                <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500 }}>
                  保存
                </Button>
              </Stack>
            </FooterActionBar>
          }
        />
      </OrganizationContainer>
    </Form>
  );
};
